import React, { useState } from 'react';
import { UserPlus, Mail, Shield, KeyRound, CheckCircle2, AlertCircle, ArrowRight, RefreshCw, Eye, EyeOff, User, Phone, Briefcase, Building2, MapPin, Hash } from 'lucide-react';
import adminService from '../../services/adminService';

const AccountProvisioning = () => {
    const [newUser, setNewUser] = useState({
        mail: '',
        username: '',
        domain: 'xevyte.com',
        password: '',
        role: 'USER',
        fullName: '',
        mobile: '',
        designation: '',
        department: '',
        address: '',
        employeeId: ''
    });
    const domains = ['xevyte.com', 'xevyte.co'];
    const [activeTab, setActiveTab] = useState('profile');
    const [isLoading, setIsLoading] = useState(false);
    const [status, setStatus] = useState({ type: null, message: '' });
    const [showPassword, setShowPassword] = useState(false);

    const handleCreateUser = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setStatus({ type: null, message: '' });

        const finalEmail = `${newUser.username}@${newUser.domain}`;
        try {
            await adminService.createUser({ ...newUser, mail: finalEmail });
            setStatus({
                type: 'success',
                message: `User account ${finalEmail} has been successfully created.`
            });
            setNewUser({
                mail: '',
                username: '',
                domain: 'xevyte.com',
                password: '',
                role: 'USER',
                fullName: '',
                mobile: '',
                designation: '',
                department: '',
                address: '',
                employeeId: ''
            });
            setShowPassword(false);
            setActiveTab('profile'); // Reset to profile tab for next user
        } catch (error) {
            setStatus({
                type: 'error',
                message: error.response?.data?.message || 'Failed to create account. Please try again.'
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto space-y-8 animate-fade-in">
            <header>
                <h1 className="text-3xl font-extrabold tracking-tight underline decoration-[#4f46e5]/30 decoration-4 underline-offset-8 uppercase tracking-wider">Create New Account</h1>
                <p className="text-slate-500 mt-2 font-medium">Easily create new user accounts and mailboxes.</p>
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Information Card */}
                <div className="lg:col-span-1 space-y-6">
                    <div className="enterprise-card p-6 bg-[#4f46e5] text-white shadow-xl shadow-[#4f46e5]/20">
                        <UserPlus size={40} className="mb-4 opacity-80" />
                        <h3 className="text-xl font-bold mb-2 tracking-tight">System Policy</h3>
                        <p className="text-blue-50 text-sm leading-relaxed font-medium">
                            Creating a new account automatically generates a mailbox and sets a starting storage limit of 100MB.
                        </p>
                    </div>

                    <div className="enterprise-card p-6 space-y-4">
                        <div className="flex items-center space-x-3 text-[#4f46e5]">
                            <Shield size={18} />
                            <span className="text-xs font-bold uppercase tracking-widest">Access Control</span>
                        </div>
                        <ul className="space-y-3">
                            <li className="flex items-start space-x-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-slate-300 mt-1.5"></span>
                                <p className="text-xs text-slate-500 font-bold uppercase tracking-tighter">Standard: Basic mail access</p>
                            </li>
                            <li className="flex items-start space-x-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-slate-300 mt-1.5"></span>
                                <p className="text-xs text-slate-500 font-bold uppercase tracking-tighter">Manager: Advanced management</p>
                            </li>
                            <li className="flex items-start space-x-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-slate-300 mt-1.5"></span>
                                <p className="text-xs text-slate-500 font-bold uppercase tracking-tighter">Admin: Full system access</p>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Provisioning Form */}
                <div className="lg:col-span-2">
                    <div className="enterprise-card p-8 glass overflow-hidden relative">
                        <div className="absolute top-0 left-0 w-full h-1 bg-[#4f46e5]"></div>

                        {status.type === 'success' ? (
                            <div className="py-12 text-center space-y-6 animate-scale-in">
                                <div className="w-20 h-20 bg-emerald-500/10 text-emerald-500 rounded-full flex items-center justify-center mx-auto border-2 border-emerald-500/20">
                                    <CheckCircle2 size={40} />
                                </div>
                                <div className="space-y-2">
                                    <h3 className="text-2xl font-black tracking-tight text-slate-900 dark:text-white uppercase">Activation Successful</h3>
                                    <p className="text-slate-500 text-sm max-w-sm mx-auto font-medium">{status.message}</p>
                                </div>
                                <button
                                    onClick={() => {
                                        setStatus({ type: null, message: '' });
                                        setActiveTab('profile');
                                    }}
                                    className="premium-button-primary px-8 rounded-xl mx-auto flex items-center group"
                                >
                                    <span>Provision Next Identity</span>
                                    <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={handleCreateUser} className="space-y-8">
                                <div className="flex p-1 bg-slate-50 dark:bg-slate-900/50 rounded-2xl w-full border border-slate-100 dark:border-slate-800/50">
                                    <button
                                        type="button"
                                        className={`flex-1 flex items-center justify-center px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-[0.15em] transition-all duration-300 ${activeTab === 'profile'
                                            ? 'bg-white dark:bg-slate-800 text-[#4f46e5] shadow-lg shadow-[#4f46e5]/5 border border-[#4f46e5]/10'
                                            : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
                                            }`}
                                        onClick={() => setActiveTab('profile')}
                                    >
                                        <User size={14} className="mr-2" />
                                        User Profile
                                    </button>
                                    <button
                                        type="button"
                                        className={`flex-1 flex items-center justify-center px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-[0.15em] transition-all duration-300 ${activeTab === 'credentials'
                                            ? 'bg-white dark:bg-slate-800 text-[#4f46e5] shadow-lg shadow-[#4f46e5]/5 border border-[#4f46e5]/10'
                                            : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
                                            }`}
                                        onClick={() => setActiveTab('credentials')}
                                    >
                                        <KeyRound size={14} className="mr-2" />
                                        Account Security
                                    </button>
                                </div>

                                {activeTab === 'profile' ? (
                                    <div className="space-y-6 animate-fade-in outline-none">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div>
                                                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3 px-1">Full User Name</label>
                                                <div className="relative group">
                                                    <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#4f46e5] transition-colors" size={18} />
                                                    <input
                                                        type="text"
                                                        required
                                                        placeholder="Full Identity Name"
                                                        className="premium-input pl-12 h-14 text-sm font-bold tracking-tight"
                                                        value={newUser.fullName}
                                                        onChange={(e) => setNewUser({ ...newUser, fullName: e.target.value })}
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3 px-1">Contact Mobile</label>
                                                <div className="relative group">
                                                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#4f46e5] transition-colors" size={18} />
                                                    <input
                                                        type="text"
                                                        placeholder="+0 000 000 000"
                                                        className="premium-input pl-12 h-14 text-sm font-bold tracking-tight"
                                                        value={newUser.mobile}
                                                        onChange={(e) => setNewUser({ ...newUser, mobile: e.target.value })}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div>
                                                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3 px-1">Job Designation</label>
                                                <div className="relative group">
                                                    <Briefcase className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#4f46e5] transition-colors" size={18} />
                                                    <input
                                                        type="text"
                                                        placeholder="Corporate Designation"
                                                        className="premium-input pl-12 h-14 text-sm font-bold tracking-tight"
                                                        value={newUser.designation}
                                                        onChange={(e) => setNewUser({ ...newUser, designation: e.target.value })}
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3 px-1">Department</label>
                                                <div className="relative group">
                                                    <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#4f46e5] transition-colors" size={18} />
                                                    <input
                                                        type="text"
                                                        placeholder="Infrastructure / Unit"
                                                        className="premium-input pl-12 h-14 text-sm font-bold tracking-tight"
                                                        value={newUser.department}
                                                        onChange={(e) => setNewUser({ ...newUser, department: e.target.value })}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div>
                                                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3 px-1">Employee ID</label>
                                                <div className="relative group">
                                                    <Hash className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#4f46e5] transition-colors" size={18} />
                                                    <input
                                                        type="text"
                                                        placeholder="EMP12345"
                                                        className="premium-input pl-12 h-14 text-sm font-bold tracking-tight"
                                                        value={newUser.employeeId}
                                                        onChange={(e) => setNewUser({ ...newUser, employeeId: e.target.value })}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3 px-1">Physical Address</label>
                                            <div className="relative group">
                                                <MapPin className="absolute left-4 top-5 text-slate-400 group-focus-within:text-[#4f46e5] transition-colors" size={18} />
                                                <textarea
                                                    placeholder="Enter full corporate or residential address..."
                                                    className="premium-input pl-12 py-4 h-24 text-sm font-bold tracking-tight resize-none"
                                                    value={newUser.address}
                                                    onChange={(e) => setNewUser({ ...newUser, address: e.target.value })}
                                                />
                                            </div>
                                        </div>
                                        <div className="pt-4 flex justify-end">
                                            <button
                                                type="button"
                                                onClick={() => setActiveTab('credentials')}
                                                className="premium-button-secondary px-6 py-3 rounded-xl flex items-center group transition-all"
                                            >
                                                <span className="text-[10px] font-black uppercase tracking-widest">Next: Access Credentials</span>
                                                <ArrowRight size={14} className="ml-2 group-hover:translate-x-1 transition-transform" />
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-6 animate-fade-in outline-none">
                                        <div>
                                            <div className="flex items-center space-x-4 mb-3 px-1">
                                                <div className="flex-1">
                                                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3">Username</label>
                                                    <div className="relative group">
                                                        <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#4f46e5] transition-colors" size={18} />
                                                        <input
                                                            type="text"
                                                            required
                                                            placeholder="identity"
                                                            className="premium-input pl-12 h-14 text-sm font-bold tracking-tight"
                                                            value={newUser.username}
                                                            onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="pt-8 text-xl font-black text-slate-300">@</div>
                                                <div className="w-[180px]">
                                                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3">Domain</label>
                                                    <div className="relative group">
                                                        <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#4f46e5] transition-colors" size={18} />
                                                        <select
                                                            className="premium-input pl-12 h-14 appearance-none cursor-pointer font-bold lowercase text-xs tracking-tight"
                                                            value={newUser.domain}
                                                            onChange={(e) => setNewUser({ ...newUser, domain: e.target.value })}
                                                        >
                                                            {domains.map(d => (
                                                                <option key={d} value={d}>{d}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div>
                                                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3 px-1">Account Access Level</label>
                                                <div className="relative group">
                                                    <Shield size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#4f46e5] transition-colors" />
                                                    <select
                                                        className="premium-input pl-12 h-14 appearance-none cursor-pointer font-bold uppercase text-[10px] tracking-widest"
                                                        value={newUser.role}
                                                        onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                                                    >
                                                        <option value="USER">Standard User</option>
                                                        <option value="MANAGER">Elevated Manager</option>
                                                        <option value="ADMIN">MTA-ROOT Administrator</option>
                                                    </select>
                                                </div>
                                            </div>
                                            <div>
                                                <div className="flex justify-between items-center mb-3 px-1">
                                                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest">Account Password</label>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*";
                                                            let pass = "";
                                                            pass += "ABCDEFGHIJKLMNOPQRSTUVWXYZ"[Math.floor(Math.random() * 26)];
                                                            pass += "abcdefghijklmnopqrstuvwxyz"[Math.floor(Math.random() * 26)];
                                                            pass += "0123456789"[Math.floor(Math.random() * 10)];
                                                            pass += "!@#$%^&*"[Math.floor(Math.random() * 8)];
                                                            for (let i = 0; i < 8; i++) pass += chars[Math.floor(Math.random() * chars.length)];
                                                            pass = pass.split('').sort(() => 0.5 - Math.random()).join('');
                                                            setNewUser({ ...newUser, password: pass });
                                                            setShowPassword(true);
                                                        }}
                                                        className="text-[9px] font-black text-[#4f46e5] uppercase tracking-[0.15em] hover:text-[#4f46e5] transition-colors flex items-center"
                                                    >
                                                        <RefreshCw size={10} className="mr-1" />
                                                        Auto-Generate
                                                    </button>
                                                </div>
                                                <div className="relative group">
                                                    <KeyRound size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#4f46e5] transition-colors" />
                                                    <input
                                                        type={showPassword ? "text" : "password"}
                                                        required
                                                        placeholder="8-16 characters"
                                                        className="premium-input pl-12 pr-12 h-14 font-mono text-sm tracking-widest"
                                                        value={newUser.password}
                                                        onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                                                    />
                                                    <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center space-x-1">
                                                        {newUser.password && (
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    navigator.clipboard.writeText(newUser.password);
                                                                    // Optional: Add a brief "copied" state/toast
                                                                }}
                                                                className="p-1.5 text-slate-400 hover:text-[#4f46e5] transition-colors"
                                                                title="Copy to clipboard"
                                                            >
                                                                <CheckCircle2 size={16} />
                                                            </button>
                                                        )}
                                                        <button
                                                            type="button"
                                                            onClick={() => setShowPassword(!showPassword)}
                                                            className="p-1.5 text-slate-400 hover:text-[#4f46e5] transition-colors"
                                                        >
                                                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 space-y-2">
                                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">Complexity Policy</p>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5">
                                                {[
                                                    { label: "8–16 Characters", valid: newUser.password.length >= 8 && newUser.password.length <= 16 },
                                                    { label: "Uppercase (A-Z)", valid: /[A-Z]/.test(newUser.password) },
                                                    { label: "Lowercase (a-z)", valid: /[a-z]/.test(newUser.password) },
                                                    { label: "Number (0-9)", valid: /[0-9]/.test(newUser.password) },
                                                    { label: "Special (!@#$%^&*)", valid: /[!@#$%^&*]/.test(newUser.password) }
                                                ].map((req, i) => (
                                                    <div key={i} className={`flex items-center space-x-2 ${req.valid ? 'text-emerald-500' : 'text-slate-400'} transition-colors`}>
                                                        {req.valid ? <CheckCircle2 size={10} /> : <div className="w-2.5 h-2.5 rounded-full border border-current opacity-30" />}
                                                        <span className="text-[9px] font-bold uppercase tracking-tight">{req.label}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="pt-4">
                                            <button
                                                type="button"
                                                onClick={() => setActiveTab('profile')}
                                                className="text-[10px] font-black text-slate-400 uppercase tracking-widest hover:text-[#4f46e5] transition-all flex items-center"
                                            >
                                                <ArrowRight size={14} className="mr-2 rotate-180" />
                                                Back to User Profile
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {status.type === 'error' && (
                                    <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center space-x-3 text-red-500 animate-slide-in">
                                        <AlertCircle size={20} className="shrink-0" />
                                        <p className="text-[11px] font-bold uppercase tracking-tight">{status.message}</p>
                                    </div>
                                )}

                                <div className="pt-4">
                                    <button
                                        type="submit"
                                        disabled={isLoading}
                                        className="premium-button-primary w-full h-14 rounded-2xl flex items-center justify-center text-[10px] font-black uppercase tracking-[0.2em] shadow-xl shadow-[#4f46e5]/30"
                                    >
                                        {isLoading ? (
                                            <>
                                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3"></div>
                                                <span>Creating Account...</span>
                                            </>
                                        ) : (
                                            <>
                                                <UserPlus size={18} className="mr-3" />
                                                <span>Create User Account</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AccountProvisioning;
